'use client';

import './PriveeLoggedOut.style.scss';
import Image from 'next/image';
import PriveeKeyholeWhite from 'public/img/icon/keyhole-white.png';
import Text from '@/components/common/Text/Text';
import Button from '@/components/common/Button/Button';
import PriveeAddOn from '@/components/common/PriveeAddOn/PriveeAddOn';
import { useRouter } from 'next/navigation';
import { getTierIconByName } from '@/util/subscription_client';
import { useModal } from '@/components/hook/modal';
import PriveeLoggedOutExpressInterestModal from '@/app/reserve/LoggedOutExpressInterestModal/PriveeLoggedOutExpressInterestModal';
import FAQSection, {
  FAQQuestionProps
} from '@/components/builder/FAQSection/FAQSection';
import {
  FallbackSubscriptionPrices,
  SubscriptionProduct,
  SubscriptionTitle
} from '@/types/Subscription';
import { ProductCardData } from '@/types/Product';
import ProductCard from '@/components/common/ProductCard/ProductCard';
import { useEffect } from 'react';

const PRIVEE_VIDEO_MWEB =
  'https://d20cg60zeycvr1.cloudfront.net/videos/privee-video-blur-1-recangle-mweb-compressed.mp4';
const PRIVEE_VIDEO_DESKTOP =
  'https://d20cg60zeycvr1.cloudfront.net/videos/privee-video-blur-1-recangle-desktop-compressed.mp4';

export const fallbackFAQs: FAQQuestionProps[] = [
  {
    question: 'How much does shipping cost?',
    answer:
      '<p><span style="font-size: 14px;">For you? Nothing! The cost of standard shipping and returns for your items is included in your monthly membership fee.</span></p><p><span style="font-size: 14px;">If you\'d like to get your item faster, select one of our expedited shipping options during checkout. We offer expedited options for an added fee, which is charged to your card on file.</span></p><p><span style="font-size: 14px;">Email us at </span><a href="mailto:membership@vivrelle.com" rel="noopener noreferrer" style="font-size: 14px;"><u>membership@vivrelle.com</u></a><span style="font-size: 14px;"> for additional information on shipping and processing times. </span></p>'
  },
  {
    question: 'Why do you require a three-month minimum membership?',
    answer:
      '<p><span style="font-size: 14px;">Vivrelle is built on loyalty. Having a three-month minimum ensures we are building a community of members who want to wear and experience a variety of luxury accessories from our closet versus joining for one-time use.</span></p>'
  },
  {
    question: "How do I know if the items I'm borrowing are authentic?",
    answer:
      '<p><span style="font-size: 14px;">Authenticity is of utmost importance to Vivrelle. Every item we offer is authenticated by the Vivrelle team&nbsp;or&nbsp;third-party experts. </span></p><p><span style="font-size: 14px;">﻿For questions regarding authenticity, please contact us at&nbsp;</span><a href="mailto:membership@vivrelle.com" rel="noopener noreferrer" style="font-size: 14px;"><u>membership@vivrelle.com</u></a><span style="font-size: 14px;">.</span></p>'
  },
  {
    question: 'What is Premier? Why should I add it to an existing membership?',
    answer:
      '<p><span style="font-size: 14px;">Premier provides members with the option to borrow jewelry with a retail price up to $1,000 or select handbags for just $49/month. </span></p><p><span style="font-size: 14px;">Members also have the ability to add this tier to any existing membership plan, to experience an additional item per month!</span></p>'
  },
  {
    question: 'I have something borrowed and I love it! Can I buy it?',
    answer:
      '<p><span style="font-size: 14px;">Absolutely! We love seeing our members fall in love with their items. As part of your membership, you receive discounted prices to purchase items from Vivrelle\'s closet. </span></p><p><span style="font-size: 14px;">On top of this, we also apply a portion of your membership fee towards the purchase. If you\'d like to inquire about buying an item, please email us at&nbsp;</span><a href="mailto:membership@vivrelle.com" rel="noopener noreferrer" style="font-size: 14px;"><u>membership@vivrelle.com</u></a><span style="font-size: 14px;">. </span></p><p><span style="font-size: 14px;">Please note all sales are final. Gift cards and membership credit cannot be applied toward purchases.   </span></p>'
  }
];

type PriveeLoggedOutProps = {
  subscriptionProducts: SubscriptionProduct[];
  faqQuestions: FAQQuestionProps[];
  priveeProducts: ProductCardData[];
};

function PriveeLoggedOut({
  subscriptionProducts,
  faqQuestions,
  priveeProducts
}: PriveeLoggedOutProps) {
  const router = useRouter();
  const { showModal, closeModal } = useModal();

  const priveePrice = (
    subscriptionProducts.find((sp) => sp.name === 'Privee')?.price ??
    FallbackSubscriptionPrices['Privee']
  ).asFormattedString.slice(0, -3);

  useEffect(() => {
    return () => {
      closeModal();
    };
  }, []);

  return (
    <div className='PriveeLoggedOut'>
      <div className='PriveeLoggedOut__hero-and-video'>
        <div className='PriveeLoggedOut__hero'>
          <h1 className='PriveeLoggedOut__hero-title'>
            The journey begins with&nbsp;Vivrelle.
          </h1>
          <p className='PriveeLoggedOut__hero-subtitle'>
            Introducing R&eacute;serv&eacute;: Vivrelle&apos;s most exclusive,
            invite-only tier.
          </p>
          <Text
            variant='text-s'
            className='PriveeLoggedOut__hero-description'
          >
            R&eacute;serv&eacute; is our most exclusive, invite-only tier,
            granting access to an ultra-curated collection of rare pieces
            reserved for approved members who have been invited to join.
          </Text>
          <div className='PriveeLoggedOut__hero-buttons'>
            <Button
              className='PriveeLoggedOut__interested-button'
              size='large'
              variant='light'
              onClick={() => {
                showModal(
                  <PriveeLoggedOutExpressInterestModal onClose={closeModal} />
                );
              }}
            >
              I&apos;m&nbsp;Interested
            </Button>
            <Button
              className='PriveeLoggedOut__about-button'
              variant='dark'
              onClick={() => router.push('/how-it-works')}
            >
              About&nbsp;Vivrelle
            </Button>
          </div>
        </div>
        <div className='PriveeLoggedOut__video-container'>
          <video
            className='PriveeLoggedOut__video'
            autoPlay
            loop
            muted
            playsInline
            preload='metadata'
          >
            <source
              src={PRIVEE_VIDEO_DESKTOP}
              media='(min-width: 900px)'
            />
            <source src={PRIVEE_VIDEO_MWEB} />
          </video>
        </div>
      </div>
      <div className='PriveeLoggedOut__introducing'>
        <h2 className='PriveeLoggedOut__introducing-header'>
          Introducing:
          <br className='PriveeLoggedOut__introducing-header-break-mobile' />{' '}
          Vivrelle R&eacute;serv&eacute;
        </h2>
        <Text
          variant='text-m'
          className='PriveeLoggedOut__introducing-description'
        >
          Réservé is an invite-only add-on tier within Vivrelle that gives you
          access to an exclusive collection of ultra high-end luxury
          accessories, including{' '}
          <span className='PriveeLoggedOut__introducing-description--bold'>
            Hermès Birkin and Kelly bags, as well as premium styles from Van
            Cleef & Arpels, the Bvlgari Serpenti collection, and more.
          </span>{' '}
          It sits on top of your existing Vivrelle membership and includes a
          separate Réservé closet. Members can borrow one Réservé item at a time
          with a 30-day swap cycle, supported by a dedicated VIP concierge.
          <br />
          <br />
          <span className='PriveeLoggedOut__introducing-description--bold'>
            R&eacute;serv&eacute; membership begins at
            <br className='PriveeLoggedOut__newline-hide-on-desktop' />
            {`\u00A0`} {priveePrice}/month,
          </span>{' '}
          designed for members seeking rare access, white-glove service, and a
          truly elevated experience.{' '}
          <span className='PriveeLoggedOut__introducing-description--bold'>
            R&eacute;serv&eacute; members also have the opportunity to purchase
            items from the R&eacute;serv&eacute; collection at preferred
            members-only pricing, with a portion of their membership fees
            applied towards their purchase.
          </span>
          <br />
          <br />
          <span className='PriveeLoggedOut__introducing-description--bold'>
            You must be a Vivrelle member to be considered for a
            R&eacute;serv&eacute; invitation. Not a Vivrelle member? Learn more
            below.
          </span>
        </Text>
        <Image
          className='PriveeLoggedOut__introducing-keyhole'
          src={PriveeKeyholeWhite}
          alt='Réservé Keyhole in white'
          width={892}
          height={1814}
        />
      </div>
      {priveeProducts.length > 0 && (
        <div className='PriveeLoggedOut__preview'>
          <h2 className='PriveeLoggedOut__preview-title'>
            R&eacute;serv&eacute; Preview
          </h2>
          <div className='PriveeLoggedOut__preview-grid'>
            {priveeProducts.slice(0, 6).map((item) => (
              <ProductCard
                data={item}
                key={item.sku}
                onProductCardClick={(e) => {
                  e.preventDefault();
                  showModal(
                    <PriveeLoggedOutExpressInterestModal onClose={closeModal} />
                  );
                }}
              />
            ))}
          </div>
          <Button
            className='PriveeLoggedOut__preview-see-more'
            variant='light'
            size='large'
            onClick={() => router.push('/collection/reserve')}
          >
            See More
          </Button>
        </div>
      )}
      <div className='PriveeLoggedOut__what-is-vivrelle'>
        <h2 className='PriveeLoggedOut__what-is-vivrelle-title'>
          What is Vivrelle?
        </h2>
        <Text
          variant='text-m'
          className='PriveeLoggedOut__what-is-vivrelle-description'
        >
          Vivrelle is a luxury accessories membership offering access to a
          curated closet you can borrow and rotate whenever you&nbsp;want.
        </Text>
      </div>
      <div className='PriveeLoggedOut__how-it-works'>
        <h2 className='PriveeLoggedOut__how-it-works-title'>How It Works</h2>
        <div className='PriveeLoggedOut__how-it-works-steps'>
          {[
            {
              stepTitle: 'Choose Your Membership',
              stepDescription:
                'Select a plan that fits your lifestyle. All memberships come with monthly swaps.'
            },
            {
              stepTitle: 'Borrow Your Favorites',
              stepDescription:
                "From Chanel to Dior, pick your dream piece and we'll ship it directly to your door. Keep it as long as you want."
            },
            {
              stepTitle: 'Rotate Anytime',
              stepDescription:
                'Ready for something new? Swap your item for another with just a few clicks.'
            }
          ].map((s, idx) => (
            <div
              className='PriveeLoggedOut__step'
              key={`${idx} ${s.stepTitle}`}
            >
              <Text
                className='PriveeLoggedOut__step-number'
                variant='title-xxs-700'
              >
                {idx + 1}
              </Text>
              <Text
                variant='title-xxs-700-upper'
                className='PriveeLoggedOut__step-title'
              >
                {s.stepTitle}
              </Text>
              <Text
                variant='text-l'
                className='PriveeLoggedOut__step-description'
              >
                {s.stepDescription}
              </Text>
            </div>
          ))}
        </div>
      </div>
      <div className='PriveeLoggedOut__tiers'>
        <h2 className='PriveeLoggedOut__tiers-title'>Tiers at a Glance</h2>
        <div className='PriveeLoggedOut__standard-tiers'>
          {(
            [
              'Classique',
              'Couture',
              'Classique+',
              'Couture+'
            ] as SubscriptionTitle[]
          ).map((tierName, idx) => (
            <div
              className='PriveeLoggedOut__standard-tier'
              key={`${idx} ${tierName}`}
            >
              <Image
                className='PriveeLoggedOut__tier-icon'
                src={getTierIconByName(tierName)}
                alt={`${tierName} icon`}
                width={30}
                height={30}
              />
              <Text className='PriveeLoggedOut__tier-title'>
                {tierName.replace('+', '')}
                {tierName.includes('+') && (
                  <span className='PriveeLoggedOut__tier-title-plus'>+</span>
                )}
              </Text>
              <Text
                variant='title-xxs-500'
                className='PriveeLoggedOut__tier-price'
              >
                {(
                  subscriptionProducts.find((sp) => sp.name === tierName)
                    ?.price ?? FallbackSubscriptionPrices[tierName]
                ).asFormattedString
                  .slice(0, -3)
                  .concat('/MONTH')}
              </Text>
              <Text
                variant='title-xxs-500'
                className='PriveeLoggedOut__items-at-a-time'
              >
                {(tierName.includes('+') ? '2 ITEMS' : '1 ITEM') + ' AT A TIME'}
              </Text>
              <Text
                variant='text-m'
                className='PriveeLoggedOut__from-which-closet'
              >
                {tierName === 'Couture+'
                  ? '1 from Classique + 1 from Couture'
                  : `from the ${tierName.replace('+', '')} closet`}
              </Text>
              <Text
                variant='text-s'
                className='PriveeLoggedOut__tier-includes'
              >
                {tierName.includes('Couture')
                  ? 'Includes access to all jewelry, bags, and limited edition pieces'
                  : 'Includes access to jewelry and bags with retail value up to $4,000'}
              </Text>
            </div>
          ))}
        </div>
        <PriveeAddOn className='PriveeLoggedOut__privee-addon' />
        <Button
          className='PriveeLoggedOut__learn-more-button'
          variant='light'
          size='large'
          onClick={() => router.push('/how-it-works')}
        >
          Learn More
        </Button>
      </div>
      {faqQuestions.length > 0 && (
        <div className='PriveeLoggedOut__faqs'>
          <FAQSection
            headline='FAQs'
            questions={faqQuestions}
          />
        </div>
      )}
    </div>
  );
}

export default PriveeLoggedOut;
